import i18n from '@quizzach/i18n';

interface ValidationRules {
	minLength?: number;
	maxLength?: number;
	regex?: RegExp;
	errorMessage: string;
	minLengthMessage?: string;
	maxLengthMessage?: string;
	getRegexMessage?: (value: string) => string;
}

/**
 * Validates input value based on provided validation rules.
 * @param name The name of the input field
 * @param value The value of the input field
 * @returns An error message if validation fails, otherwise an empty string
 */

export const validateInput = (name: string, value: string): string => {
	const validationRules: ValidationRules = getValidationRules(name);

	if (value === undefined || value.trim() === '') {
		return i18n.t('validation.required');
	}

	if (validationRules.regex && !validationRules.regex.test(value)) {
		return validationRules.getRegexMessage?.(value) ?? validationRules.errorMessage;
	}

	if (validationRules.minLength && value.length < validationRules.minLength) {
		return validationRules.minLengthMessage ?? validationRules.errorMessage;
	}

	if (validationRules.maxLength && value.length > validationRules.maxLength) {
		return validationRules.maxLengthMessage ?? validationRules.errorMessage;
	}

	return '';
};

/**
 * Retrieves validation rules for a given input field name.
 * @param name The name of the input field
 * @returns The validation rules for the specified input field
 */

const getValidationRules = (name: string): ValidationRules => {
	switch (name) {
		case 'email':
			return {
				minLength: 5,
				maxLength: 40,
				regex:
					/^[a-zA-Z0-9+._%\-+]+@[a-zA-Z0-9][a-zA-Z0-9-]{0,64}(\.[a-zA-Z0-9][a-zA-Z0-9-]{0,25})+$/,
				errorMessage: i18n.t('validation.email'),
			};
		case 'password':
			return {
				minLength: 10,
				maxLength: 40,
				regex: /^(?=.*[0-9])(?=.*[a-z])(?=.*[A-Z])(?=.*[\W_])(?!.* ).{10,}$/,
				errorMessage: i18n.t('validation.password'),
			};
		case 'username':
			return {
				minLength: 3,
				maxLength: 20,
				regex: /^[A-Za-z0-9.]+$/,
				errorMessage: i18n.t('validation.usernameChars'),
				minLengthMessage: i18n.t('validation.usernameShort'),
				maxLengthMessage: i18n.t('validation.usernameLong'),
				getRegexMessage: (value) =>
					/[čćđšžČĆĐŠŽ]/.test(value)
						? i18n.t('validation.usernameCroatian')
						: i18n.t('validation.usernameChars'),
			};

		default:
			return { errorMessage: '' };
	}
};
